import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import styled from 'styled-components';
import { withRouter } from 'react-router-dom';
import * as qs from 'qs';
import { Box, CircularProgress } from '@material-ui/core';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@mui/material/Alert';
import { formatValue } from 'react-currency-input-field';
import Accordion from '../components/accordion';
import AccordionItem from '../components/accordion-item';
import Header from '../components/TopHeader';
import i18n from '../i18n/i18n';
import { checkSessionStatus, getCustomerPurchasesHistory, getSlaveInfo } from '../api/api';
import PrivateCardSlider from '../components/private-card-slider';
import ActionButtons from '../components/ActionButtons';
import GalpUserMenu from '../components/GalpUserMenu';
import { useVendingMachine } from '../contexts/VendingMachineContext';
import { useUser } from '../contexts/UserContext';
import { Purchase } from '../types/types';
import { getDate, getTime } from '../utils/DateFormatter';
import ProductListView from './ProductListView';
import getImageFromGateway from '../utils/GatewayImages';
import { useSession } from '../contexts/SessionContext';
import { useTheme } from '../contexts/ThemeContext';
import toSessionIdParam from '../utils/sessionId';
import SelectSocket from './SelectSocket';
import {
  clearSelection, getLiveSessionCandidate, getPickerParentId, getPickerScopeLeaves, getPickerScopeRoot,
  getSlaveGateStep, isLiveSessionState, isSlaveAvailable, selectOnly,
} from '../utils/slaveSelection';
import { getSpgPaymentMethods, SPG_CARD, SPG_MBWAY } from '../utils/spgPaymentMethods';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  width: 100%;
  height: 100%;
  overflow-y: scroll;
`;

const ListFiltersContainer = styled.div`
  justify-content: start;
  display: flex;
  flex-direction: row;
  align-items: start;
  margin-top: 1%;
  width: 100%;
  height: 11%;
  min-height: 6dvh;
`;

const FilteredList = styled.div`
  display: flex;
  padding-top: 4px;
  align-content: flex-start;
  justify-content: center;
  flex-wrap: wrap;
  width: 100%;
  overflow-y: auto;
  position: relative;
  background: white;
  flex-direction: row;
  align-items: center;
  margin: auto;
  height: 88%;
`;

const ListItemsContainer = styled.div`
  display: flex;
  align-content: center;
  flex-wrap: wrap;
  width: 100%;
  flex: 1;
  overflow: auto;
  flex-direction: column;
`;

const LogoContainer = styled.div`
  width: 100%;
  height: 5dvh;
  display: flex;
  justify-content: center;
  bottom: 0;
  position: fixed;
  background-color: white;
  padding: 10px;
  box-shadow: rgba(0, 0, 0, 0.6) 0 2px 5px;
`;

const FilterButton = styled.div<{ selected?: boolean, bgColor?: string }>`
  flex: 1;
  padding: 2%;
  background: ${(props) => (props.selected ? props.bgColor || 'rgb(141,197,62)' : 'rgb(241,241,241)')};
  text-align: center;
  margin-top: 2%;
  margin-bottom: 2%;
  border-radius: 6px;
  color: ${(props) => (props.selected ? 'rgb(255,255,255)' : 'rgb(161,161,161)')};
  font-size: 1em;
`;

const Spacer = styled.div<any>`
  min-width: ${(props: any) => (props.width ? props.width : '10px')};
`;

const Label = styled.span`
  font-size: 0.85em;
  min-width: 110px;
  display: inline-block;
`;

const BoldLabel = styled.span`
  font-size: 0.85em;
  font-weight: bold;
  min-width: 110px;
  display: inline-block;
  width: 40%;
`;

const StyledSpan = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  max-height: 30px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

const VMView = ({ history, location }) => {
  const [dummy, setDummy] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [paymentMethod, setPaymentMethod] = useState<any>(undefined);
  const [vendingMachinePaymentMethods, setVendingMachinePaymentMethods] = useState<any>([]);
  const [value, setValue] = React.useState(0);
  const {
    vendingMachineInfo, setVendingMachineInfo, stickerSlaveId, confirmedSlaveId, setConfirmedSlaveId,
  } = useVendingMachine();
  const [vmProducts, setVmProducts] = useState<any[]>([]);
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [error, setError] = useState<boolean>(false);
  const [couldNotLoadPurchases, setCouldNotLoadPurchases] = useState<boolean>(false);
  // EV chargers resolve their connector before the payment methods show
  const [slaveGate, setSlaveGate] = useState<'checking' | 'picker' | 'ready'>('checking');
  const [pickerParentId, setPickerParentId] = useState<string | null>(null);
  const [pickerNotice, setPickerNotice] = useState<string | null>(null);
  // The session the server said is no longer live, so its connector gets the normal check
  const [endedSessionId, setEndedSessionId] = useState<string | null>(null);
  const isEvCharger = vendingMachineInfo?.equipmentType?.label === 'EV Charger';
  const { theme, themeName } = useTheme();
  const { user } = useUser();
  const isGalp = themeName === 'galp';

  const galpInitials = (() => {
    const authUser = (user as any)?.user;
    const source = authUser?.name || (authUser?.email || '').split('@')[0];
    const parts = source.split(/[\s._-]+/).filter(Boolean);
    const raw = parts.length >= 2 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : (parts[0]?.[0] || '');
    return raw.toUpperCase();
  })();

  // The tinted background is as tall as the content, which grows after mount (payment methods,
  // purchases); follow the content's size instead of measuring it once per render
  const [backgroundHeight, setBackgroundHeight] = useState<number | string>('57%');
  const contentObserver = useRef<ResizeObserver | null>(null);
  const backRef = useCallback((element: HTMLDivElement | null) => {
    if (contentObserver.current) {
      contentObserver.current.disconnect();
      contentObserver.current = null;
    }
    if (!element) {
      return;
    }
    setBackgroundHeight(element.clientHeight + 40);
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    contentObserver.current = new ResizeObserver(() => setBackgroundHeight(element.clientHeight + 40));
    contentObserver.current.observe(element);
  }, []);
  const { s, sid } = qs.parse(location.search, { ignoreQueryPrefix: true });

  const currentLanguage = useMemo(
    () => `${i18n.language[0]}${i18n.language[1]}-${i18n.language[2]}${i18n.language[3]}`,
    [i18n.language],
  );
  const { stickerId } = useVendingMachine();
  const {
    selectPaymentMethod, sessionId, sessionError, sessionSlaveId, forgetSessionSlave,
  } = useSession();
  const [cardError, setCardError] = useState<string | null>(null);
  // Reads the session refs at call time. Stable: session changes reach the gate only through
  // confirmedSlaveId and remounts, never by re-running it
  const readSession = useCallback(
    () => ({ sessionId: sessionId.current, sessionSlaveId: sessionSlaveId.current }),
    [sessionId, sessionSlaveId],
  );

  const openPicker = (parentId: string | null, notice: string | null) => {
    setPickerParentId(parentId);
    setPickerNotice(notice);
    setSlaveGate('picker');
  };

  // Record the checked connector and go on. Batched: callers run inside a promise callback or after
  // an await, where React 16 does not batch, and an intermediate commit would re-run the gate
  const confirmSlave = useCallback((slaveId: string) => batchedUpdates(() => {
    setConfirmedSlaveId(slaveId);
    if (vendingMachineInfo) {
      setVendingMachineInfo(selectOnly(vendingMachineInfo, slaveId));
    }
    setSlaveGate('ready');
  }), [vendingMachineInfo, setConfirmedSlaveId, setVendingMachineInfo]);

  useEffect(() => {
    // A fresh /load, or a session opening, drops the confirmation: resolve the connector again
    if (slaveGate === 'ready' && isEvCharger && vendingMachineInfo?.vendingMachineSlaves?.length && !confirmedSlaveId) {
      setSlaveGate('checking');
      return undefined;
    }

    if (slaveGate !== 'checking' || !vendingMachineInfo) {
      return undefined;
    }

    if (!isEvCharger) {
      setSlaveGate('ready');
      return undefined;
    }

    // During the customer's own session its connector is busy with them: before resolving the sticker or
    // opening the picker, ask the server whether that session is still live
    const session = readSession();
    const sessionCandidate = confirmedSlaveId ? null : getLiveSessionCandidate(
      vendingMachineInfo.vendingMachineSlaves,
      stickerSlaveId,
      session,
    );
    if (sessionCandidate && endedSessionId !== session.sessionId) {
      const openSessionId = session.sessionId;
      let cancelledSession = false;
      // Inside the promise, so a bad id also lands in catch and falls back to the normal flow
      Promise.resolve()
        .then(() => checkSessionStatus(toSessionIdParam(openSessionId)))
        .then((response: any) => {
          if (cancelledSession) return;
          if (isLiveSessionState(response?.sessionState?.state)) {
            // Their charge is running: resume it (the running-session screen polls it by sid) instead of
            // offering the payment cards, where paying again would reset the live session
            const sticker = stickerId ? `s=${stickerId}&` : '';
            history.replace(`/product-selection?${sticker}sid=${toSessionIdParam(openSessionId)}`);
          } else {
            forgetSessionSlave();
            setEndedSessionId(openSessionId);
          }
        })
        .catch(() => {
          if (!cancelledSession) setEndedSessionId(openSessionId);
        });
      return () => {
        cancelledSession = true;
      };
    }

    const step = getSlaveGateStep(vendingMachineInfo.vendingMachineSlaves, { stickerSlaveId, confirmedSlaveId });

    if ('ready' in step) {
      setSlaveGate('ready');
      return undefined;
    }

    if ('pickerParentId' in step) {
      openPicker(step.pickerParentId, null);
      return undefined;
    }

    // Sticker-bound or only connector: still refuse one that is in use
    const { check: leafId, parentId } = step;
    let cancelled = false;

    // React 16 does not batch state updates inside a promise callback: batch them, so no
    // intermediate commit re-runs this effect
    const refuse = (notice: string) => batchedUpdates(() => {
      // Do not label the refused connector as chosen; the sticker binding stays in stickerSlaveId
      setVendingMachineInfo(clearSelection(vendingMachineInfo));
      openPicker(parentId, notice);
    });

    getSlaveInfo(leafId)
      .then((response) => {
        if (cancelled) return;
        if (isSlaveAvailable(response.state)) {
          confirmSlave(leafId);
        } else {
          refuse('socket_in_use');
        }
      })
      .catch(() => {
        if (!cancelled) refuse('error_occured_try_later');
      });

    return () => {
      cancelled = true;
    };
  }, [vendingMachineInfo, isEvCharger, slaveGate, stickerSlaveId, confirmedSlaveId,
    setVendingMachineInfo, confirmSlave, endedSessionId, history, stickerId, forgetSessionSlave, readSession]);

  const handleChange = (newValue: number) => {
    setValue(newValue);
  };

  useEffect(() => {

    if (sid && sessionId.current) {
      checkSessionStatus(toSessionIdParam(sessionId.current));
    }

    if (!vendingMachineInfo && !s) {
      history.push('/home-no-vm');
    } else if (!vendingMachineInfo && s) {
      history.push(`/load?s=${s}`);
    }

    if (vendingMachineInfo?.products) {
      setVmProducts(vendingMachineInfo.products);
    }

    (async () => {
      if (vendingMachineInfo?.loginType === 'ANONYMOUS') {
        // wont be logged in for sure, don't try to load purchases history
        setLoading(false);
        return;
      }

      try {
        const purchasesList = await getCustomerPurchasesHistory(vendingMachineInfo?.id);
        setPurchases(purchasesList);
        setLoading(false);
      } catch (err: any) {
        if (err.status === 401 && vendingMachineInfo?.loginType === 'PUBLIC') {
          // is not logged in
          setLoading(false);
          setCouldNotLoadPurchases(true);
        } else {
          setLoading(false);
          setError(true);
        }
      } finally {
        setTimeout(() : void => {
          setDummy(!dummy);
        }, 200);
      }
    })();
  }, [vendingMachineInfo]);

  useEffect(() => {
    const artificialPaymentMethods: any[] = [];

    if (!vendingMachineInfo) {
      setShowNotification(true);
    }

    const paymentMethods: any = vendingMachineInfo?.paymentTerminals
      .filter((filteredPaymentMethod) => ['TOKEN', 'SPG', 'SPGCARD'].indexOf(filteredPaymentMethod.gatewayName) !== -1);

    if (paymentMethods?.length > 0) {
      paymentMethods.map((individualPaymentMethod: any) => {
        switch (individualPaymentMethod.gatewayName) {
          case 'TOKEN': {
            const tokenWallets: any[] = individualPaymentMethod?.wallets;
            if (tokenWallets) {
              for (let i = 0; i < tokenWallets.length; i += 1) {
                artificialPaymentMethods.push({
                  ...individualPaymentMethod,
                  wallets: undefined,
                  wallet: tokenWallets[i],
                });
              }
            }
            break;
          }
          case 'SPG': {
            // Only the methods the machine's SPG gateway cashless allows
            const spgPaymentMethods = getSpgPaymentMethods(individualPaymentMethod);
            if (spgPaymentMethods.includes(SPG_MBWAY)) {
              artificialPaymentMethods.push({
                ...individualPaymentMethod,
                wallet: null,
              });
            }
            // Online card via SIBS SPG (method: CARD). 'SPGCARD' here is only a render
            // token reused by PrivateCard/PrivateCardSlider; selection maps it to
            // gatewayName 'SPG' + gatewayParameters { method: 'CARD' } (see startCardPayment).
            if (spgPaymentMethods.includes(SPG_CARD)) {
              artificialPaymentMethods.push({
                ...individualPaymentMethod,
                gatewayName: 'SPGCARD',
                wallet: null,
              });
            }
            break;
          }
          case 'SPGCARD': {
            artificialPaymentMethods.push({
              ...individualPaymentMethod,
              wallet: null,
            });
            break;
          }
          default:
            break;
        }

        return individualPaymentMethod;
      });
    }

    setPaymentMethod(artificialPaymentMethods?.[0]);
    setVendingMachinePaymentMethods(artificialPaymentMethods);
  }, [vendingMachineInfo]);

  const shouldHidePurchaseList = couldNotLoadPurchases || vendingMachineInfo?.loginType === 'ANONYMOUS' || themeName === 'helexia';
  // The picker stays inside the sticker's scope, so there is only something to pick with two connectors there
  const pickerScopeRootId = getPickerScopeRoot(vendingMachineInfo?.vendingMachineSlaves, stickerSlaveId);
  const canPickSlave = isEvCharger
    && getPickerScopeLeaves(vendingMachineInfo?.vendingMachineSlaves, stickerSlaveId).length > 1;

  // Opens an SPG session in CARD mode and, once the server returns the SIBS
  // form context, navigates to the hosted-form screen. Used by both the EV
  // card tap (below) and the non-EV "shop" action in ActionButtons.
  const startCardPayment = async () => {
    try {
      const res = await selectPaymentMethod({
        gatewayName: 'SPG',
        gatewayParameters: { method: 'CARD' },
      });
      if (res?.spgCard) {
        history.push(`/spgcard?s=${stickerId}`, { spgCard: res.spgCard });
      } else {
        setCardError(sessionError?.current || i18n.t('card_payment_error'));
      }
    } catch (e) {
      setCardError(i18n.t('card_payment_error'));
    }
  };

  const handleBack = () => {
    openPicker(getPickerParentId(vendingMachineInfo?.vendingMachineSlaves, confirmedSlaveId), null);
    setConfirmedSlaveId(null);
    if (vendingMachineInfo) {
      setVendingMachineInfo(clearSelection(vendingMachineInfo));
    }
  };

  if (isEvCharger && slaveGate === 'picker') {
    return (
      <SelectSocket
        initialParentId={pickerParentId}
        scopeRootId={pickerScopeRootId}
        initialNotice={pickerNotice}
        onSelected={confirmSlave}
      />
    );
  }

  if (isEvCharger && slaveGate === 'checking') {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100dvh">
        <CircularProgress style={{ color: theme.loadingIconColor }} />
      </Box>
    );
  }

  if (isGalp && isEvCharger) {
    const handleGalpCardClick = (index) => {
      const selected = vendingMachinePaymentMethods[index] || paymentMethod;
      if (selected?.gatewayName === 'SPG' || selected?.gatewayName === 'MBWAY') {
        history.push(`/mbway?s=${stickerId}`, { selectedPaymentMethod: selected });
      } else if (selected?.gatewayName === 'SPGCARD') {
        selectPaymentMethod({ gatewayName: 'SPGCARD' });
        history.push(`/product-selection?s=${stickerId}`);
      } else {
        selectPaymentMethod({
          gatewayName: 'TOKEN',
          gatewayParameters: { walletId: selected?.wallet?.id },
          maxCredit: selected?.wallet?.maxCredit,
        }, selected?.wallet?.balance);
        history.push(`/product-selection?s=${stickerId}`);
      }
    };

    return (
      <div style={{
        width: '100%',
        height: '100dvh',
        background: '#d7cec5',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
      >
        {/* Header */}
        <div style={{
          position: 'relative',
          background: '#f7f4ed',
          minHeight: '94px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 16px',
          borderBottomLeftRadius: '14px',
          borderBottomRightRadius: '14px',
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
        >
          <button
            type="button"
            aria-label="back"
            onClick={() => (canPickSlave ? handleBack() : history.goBack())}
            style={{
              position: 'absolute',
              left: '16px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M6 1L1 6L6 11"
                stroke="#0b0b0b"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <p style={{
            fontFamily: theme.titleFont,
            fontSize: '16px',
            fontWeight: 500,
            lineHeight: '24px',
            color: '#0b0b0b',
            margin: 0,
            padding: '0 40px',
            textAlign: 'center',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}
          >
            {vendingMachineInfo?.name || ''}
          </p>
          <GalpUserMenu initials={galpInitials} />
        </div>

        {/* Payment method cards */}
        <div style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}
        >
          <PrivateCardSlider
            paymentMethods={vendingMachinePaymentMethods}
            loading={loading}
            showNotification={showNotification}
            onClick={handleGalpCardClick}
            onWalletChange={(selectedWallet) => setPaymentMethod(selectedWallet)}
            isInVendingMachine
            color={theme.buttonColor}
          />
        </div>
      </div>
    );
  }

  // @ts-ignore
  return (
    <Root style={{
      background: themeName === 'helexia' ? theme.vmViewBGColor : '#d9d9d9',
      zIndex: 0,
      paddingBottom: themeName === 'helexia' ? '46px' : undefined,
    }}
    >
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={5000}
        open={!!cardError}
        onClose={() => setCardError(null)}
      >
        <Alert
          onClose={() => setCardError(null)}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {cardError}
        </Alert>
      </Snackbar>
      {themeName !== 'helexia' && (
        <div style={{
          background: theme.vmViewBGColor,
          position: 'absolute',
          width: '100%',
          height: backgroundHeight,
          zIndex: 0,
          overflowY: 'scroll',
          opacity: 0.5,
        }}
        />
      )}
      <div
        ref={backRef}
        style={{
          flex: 5,
        }}
      >
        <Header
          marginBottom="20px"
          showVm
          userLogo
          cards
          color={themeName === 'helexia' ? theme.iconColor : theme.buttonColor}
          showBackButton={canPickSlave}
          backButtonColor={theme.buttonColor}
          onBack={handleBack}
        />
        <PrivateCardSlider
          paymentMethods={vendingMachinePaymentMethods}
          loading={loading}
          showNotification={showNotification}
          onClick={(index) => {
            if (isEvCharger) {
              const selectedPaymentMethod = vendingMachinePaymentMethods[index] || paymentMethod;
              if (selectedPaymentMethod?.gatewayName === 'SPG' || selectedPaymentMethod?.gatewayName === 'MBWAY') {
                history.push(`/mbway?s=${stickerId}`, { selectedPaymentMethod });
              } else if (selectedPaymentMethod?.gatewayName === 'SPGCARD') {
                startCardPayment();
              } else {
                selectPaymentMethod({
                  gatewayName: 'TOKEN',
                  gatewayParameters: { walletId: selectedPaymentMethod?.wallet?.id },
                  maxCredit: selectedPaymentMethod?.wallet?.maxCredit,
                }, selectedPaymentMethod?.wallet?.balance);
                history.push(`/product-selection?s=${stickerId}`);
              }
            }
          }}
          onWalletChange={(selectedWallet) => setPaymentMethod(selectedWallet)}
          isInVendingMachine
          color="rgb(141,197,62)"
        />
        {
          !isEvCharger && (
            <ActionButtons
              history={history}
              paymentMethod={paymentMethod}
              menu="VmMenu"
              onError={setCardError}
            />
          )
        }
      </div>
      {(!isEvCharger || !shouldHidePurchaseList)
        && (
        <div style={{
          flex: '6 1 1px',
          background: 'rgb(217 217 217)',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '0',
          marginTop: '16px',
        }}
        >
          <ListItemsContainer>
            <div style={{
              height: '100%',
              width: '96%',
              overflow: 'hidden',
              paddingTop: '2px',
              borderRadius: '12px 12px 0 0',
              background: 'white',
              zIndex: 1,
            }}
            >
              <ListFiltersContainer>
                <div style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  alignItems: 'start',
                  width: '100%',
                  paddingBottom: '10px',
                  fontSize: '0.8em',
                  paddingRight: '16px',
                  paddingLeft: '16px',
                }}
                >
                  {vendingMachineInfo?.equipmentType?.label !== 'EV Charger'
                  && (
                    <FilterButton bgColor={theme.buttonColor} selected={(value === 0)} onClick={() => handleChange(0)}>
                      {i18n.t('products')}
                    </FilterButton>
                  )}
                  {
                  (
                    vendingMachineInfo?.equipmentType?.label !== 'EV Charger' && !shouldHidePurchaseList) && (
                    <Spacer />
                  )
                }
                  {
                    !shouldHidePurchaseList && (
                      <>
                        <FilterButton
                          bgColor={theme.buttonColor}
                          selected={(value === 1)}
                          onClick={() => handleChange(1)}
                        >
                          {i18n.t('purchases')}
                        </FilterButton>
                      </>
                    )
                  }
                </div>
              </ListFiltersContainer>
              <FilteredList
                style={{
                  alignContent: vmProducts?.length === 0 ? 'center' : 'flex-start',
                }}
              >
                {vendingMachineInfo?.equipmentType?.label !== 'EV Charger'
                && (
                  <>
                    {
                      value === 0 && vmProducts?.length === 0 && (
                        <>
                          <div style={{ flex: 1 }} />
                          <h2 style={{ textAlign: 'center' }}>{i18n.t('no_products_available')}</h2>
                          <div style={{ flex: 1 }} />
                        </>
                      )
                    }
                    {value === 0 && <ProductListView location={location} currentLanguage={currentLanguage} />}
                  </>
                )}
                {
                  !shouldHidePurchaseList && (
                    <>
                      {value === 1 && !error && loading && (
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          flexDirection="column"
                          width="100%"
                          marginTop="25%"
                        >
                          <CircularProgress style={{ color: theme.loadingIconColor }} />
                        </Box>
                      )}
                      {!error && value === 1 && purchases?.length > 0 && (
                        <Accordion
                          key={3459384}
                        >
                          {purchases.map((p: any) => {
                            const revalueAmount = p?.currency === 'XXX'
                              ? p.amount
                              : (p.amount / 100)
                                .toFixed(2);

                            return (
                              <AccordionItem
                                chevronMarginRight="10px"
                                highlightColor="rgba(99,100,103,255)"
                                header={(
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'row',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      width: '100% !important',
                                      flex: 1,
                                      height: '100%',
                                    }}
                                  >
                                    <div style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      width: '40%',
                                      justifyContent: 'start',
                                      alignItems: 'start',
                                      height: '100%',
                                      fontSize: '0.85em',
                                    }}
                                    >
                                      <StyledSpan>{getDate(p.date)}</StyledSpan>
                                      <StyledSpan>{getTime(p.date)}</StyledSpan>
                                    </div>
                                    <div style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      width: '35%',
                                      justifyContent: 'center',
                                      alignItems: 'start',
                                      height: '100%',
                                      fontSize: '0.85em',
                                    }}
                                    >
                                      <StyledSpan>
                                        {p?.currency === 'XXX'
                                          ? `${revalueAmount} ${i18n.t('services')}`
                                          : formatValue({
                                            value: revalueAmount,
                                            groupSeparator: ',',
                                            decimalSeparator: '.',
                                            decimalScale: 2,
                                            intlConfig: { locale: currentLanguage, currency: p?.currency || 'EUR' },
                                          })}
                                      </StyledSpan>
                                    </div>
                                    <div style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      width: '25%',
                                      justifyContent: 'center',
                                      alignItems: 'start',
                                      height: '100%',
                                      fontSize: '0.85em',
                                    }}
                                    >
                                      {getImageFromGateway(p.gateway?.name)}
                                    </div>
                                  </div>
                                )}
                              >
                                <div style={{ width: 'calc(100% - 34px)' }}>
                                  <Row>
                                    <BoldLabel>{i18n.t('product')}</BoldLabel>
                                    <Label style={{
                                      display: 'inline-block',
                                      maxWidth: '60%',
                                      verticalAlign: 'top',
                                    }}
                                    >
                                      {p.productName}
                                    </Label>
                                  </Row>
                                  <Row>
                                    <BoldLabel>{i18n.t('machine')}</BoldLabel>
                                    <Label>{p.vendingMachineName}</Label>
                                  </Row>
                                  <Row>
                                    <BoldLabel>{i18n.t('merchant')}</BoldLabel>
                                    <Label>{p.merchantName}</Label>
                                  </Row>
                                </div>
                              </AccordionItem>
                            );
                          })}
                        </Accordion>
                      )}
                      {!error && value === 1 && purchases && purchases.length === 0 && (
                        <>
                          <div style={{ flex: 1 }} />
                          <h2 style={{ textAlign: 'center' }}>{i18n.t('no_purchases_registered')}</h2>
                          <div style={{ flex: 1 }} />
                        </>
                      )}
                      {error && (
                        <>
                          <h2 style={{ textAlign: 'center' }}>{i18n.t('error_ocurred')}</h2>
                          <h2 style={{ textAlign: 'center' }}>{i18n.t('try_again_later')}</h2>
                        </>
                      )}
                    </>
                  )
                }
              </FilteredList>
            </div>
          </ListItemsContainer>
        </div>
        )}
      {theme.logo && themeName !== 'helexia' && (
        <LogoContainer>
          <img
            src={theme.logo}
            alt="logo"
          />
        </LogoContainer>
      )}
    </Root>
  );
};

export default withRouter(VMView);
